<template>
  <Page
    :title="isEdit ? 'Edit Payment Schedule' : 'New Payment Schedule'"
    subpage
  >
    <Card title="Payment Schedule Details">
      <form data-cy="schedule-form" @submit.prevent="handleSubmit">
        <div class="mb-3">
          <label class="form-label">Name</label>
          <input
            v-model="form.name"
            type="text"
            class="form-control"
            required
            data-cy="schedule-name"
          />
        </div>
        <div class="mb-3">
          <label class="form-label">Description</label>
          <input
            v-model="form.description"
            type="text"
            class="form-control"
            data-cy="schedule-description"
          />
        </div>

        <h6 class="mt-4 mb-3">Installments</h6>
        <ProgressBillingInstallmentRow
          v-for="(installment, idx) in visibleInstallments"
          :key="idx"
          :installment="installment"
          :removable="visibleInstallments.length > 2"
          @remove="removeInstallment(idx)"
          @update:label="installment.label = $event"
          @update:percentage="installment.percentage = $event"
        />

        <div class="d-flex justify-content-between align-items-center mt-2">
          <ItemAdder
            slim
            transparent
            text="Add Installment"
            class="flex-1 mr-3"
            data-cy="add-installment"
            @click="addInstallment"
          />
          <span
            class="small whitespace-nowrap"
            :class="percentageTotal === 100 ? 'text-success' : 'text-danger'"
          >
            Total: {{ percentageTotal }}%
          </span>
        </div>

        <div v-if="errorMessage" class="alert alert-danger mt-3">
          {{ errorMessage }}
        </div>

        <div class="d-flex gap-2 mt-4">
          <Button
            text="Save"
            type="submit"
            :disabled="saving"
            data-cy="save-schedule"
          />
          <Button text="Cancel" variant="outline" @click="handleCancel" />
        </div>
      </form>
    </Card>
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import Button from "@/components/Button.vue";
import Card from "@/components/Card.vue";
import ItemAdder from "@/components/ItemAdder.vue";
import Page from "@/components/Page.vue";
import { paymentScheduleIndexPath } from "@/mixins/path_helpers";
import ProgressBillingInstallmentRow from "./ProgressBillingInstallmentRow.vue";

interface InstallmentForm {
  id?: number;
  label: string;
  percentage: number | null;
  position: number;
  _destroy?: boolean;
}

const store = useStore();
const route = useRoute();
const router = useRouter();

const isEdit = computed(() => !!route.params.id);
const saving = ref(false);
const errorMessage = ref("");

const form = reactive({
  name: "",
  description: "",
  installments: [
    { label: "Deposit", percentage: 50, position: 0 },
    { label: "Completion", percentage: 50, position: 1 }
  ] as InstallmentForm[]
});

const visibleInstallments = computed(() =>
  form.installments.filter((i) => !i._destroy)
);

const percentageTotal = computed(() => {
  return visibleInstallments.value.reduce(
    (sum, i) => sum + (Number(i.percentage) || 0),
    0
  );
});

function addInstallment() {
  form.installments.push({
    label: "",
    percentage: null,
    position: visibleInstallments.value.length
  });
}

function removeInstallment(visibleIdx: number) {
  const installment = visibleInstallments.value[visibleIdx];
  if (installment.id) {
    installment._destroy = true;
  } else {
    const realIdx = form.installments.indexOf(installment);
    form.installments.splice(realIdx, 1);
  }
  reorderPositions();
}

function reorderPositions() {
  let pos = 0;
  for (const inst of form.installments) {
    if (!inst._destroy) {
      inst.position = pos++;
    }
  }
}

async function handleSubmit() {
  saving.value = true;
  errorMessage.value = "";
  reorderPositions();

  try {
    const params = {
      name: form.name,
      description: form.description,
      installments_attributes: form.installments.map((i) => ({
        ...(i.id ? { id: i.id } : {}),
        label: i.label,
        percentage: i.percentage,
        position: i.position,
        ...(i._destroy ? { _destroy: true } : {})
      }))
    };

    if (isEdit.value) {
      await store.dispatch("updateProgressBillingSchedule", {
        id: Number(route.params.id),
        ...params
      });
    } else {
      await store.dispatch("createProgressBillingSchedule", params);
    }
    router.push(paymentScheduleIndexPath());
  } catch (err: unknown) {
    const error = err as { response?: { data?: { errors?: string[] } } };
    errorMessage.value =
      error.response?.data?.errors?.join(", ") || "Failed to save schedule";
  } finally {
    saving.value = false;
  }
}

function handleCancel() {
  router.push(paymentScheduleIndexPath());
}

onMounted(async () => {
  if (isEdit.value) {
    const schedule = await store.dispatch(
      "fetchProgressBillingSchedule",
      Number(route.params.id)
    );
    form.name = schedule.name;
    form.description = schedule.description || "";
    form.installments = schedule.installments.map(
      (i: {
        id: number;
        label: string;
        percentage: number;
        position: number;
      }) => ({
        id: i.id,
        label: i.label,
        percentage: i.percentage,
        position: i.position
      })
    );
  }
});
</script>
